import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { SelectionButton } from '@/components/PatientTypeButton';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const slideInLeft = {
  initial: { opacity: 0, x: -50 },
  animate: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: 'easeOut' },
  },
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

const buttonHover = {
  scale: 1.02,
  transition: { duration: 0.2 },
};

const buttonTap = {
  scale: 0.98,
  transition: { duration: 0.1 },
};

export function PatientVisitPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { clinic, clinicLoading, clinicError } = useClinicConfigContext();
  const [selectedPatientType, setSelectedPatientType] = useState<
    'returning' | 'new' | null
  >(null);
  // Cookie helpers
  const setCookie = (name: string, value: string) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=604800`;
  };

  // Document title
  useDocumentTitle(clinic?.name);

  const handleSelect = (patientType: 'returning' | 'new') => {
    setSelectedPatientType(patientType);
    if (!clinicSlug) return;
    const isReturningPatient = patientType === 'returning';
    // Store selection in cookies for later steps
    setCookie('patient_type', patientType);
    setCookie('is_returning_patient', String(isReturningPatient));
    // Navigate to next step immediately
    navigate(`/${clinicSlug}/basic-info`);
  };

  // No explicit Next action; navigation happens on selection

  if (clinicLoading) {
    return (
      <motion.div
        className='min-h-screen relative'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </motion.div>
    );
  }

  if (clinicError || (!clinic && !clinicLoading)) {
    return (
      <motion.div
        className='min-h-screen relative flex items-center justify-center'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          className='text-center'
          variants={staggerContainer}
          initial='initial'
          animate='animate'
        >
          <motion.h1
            className='text-4xl font-bold text-gray-800 mb-4'
            variants={fadeInUp}
          >
            404
          </motion.h1>
          <motion.p className='text-xl text-gray-600 mb-8' variants={fadeInUp}>
            Clinic not found
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className='lg:h-screen h-full w-screen overflow-hidden relative flex flex-col'
      variants={pageVariants}
      initial='initial'
      animate='animate'
      exit='exit'
      transition={{ duration: 0.6 }}
    >
      {/* Header with Logo */}
      <motion.div
        className='flex-shrink-0 p-6'
        variants={slideInLeft}
        initial='initial'
        animate='animate'
      >
        <div className='flex items-center'>
          <motion.div
            className='w-fit'
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <img
              src={'/logo.png'}
              alt={`${clinic?.name || 'Clinic'} logo`}
              className='w-full h-full object-contain'
            />
          </motion.div>
        </div>
      </motion.div>

      {/* Main Content */}
      <motion.div
        className='flex-1 flex flex-col justify-center px-6 py-8'
        variants={staggerContainer}
        initial='initial'
        animate='animate'
      >
        <div className='max-w-3xl mx-auto w-full'>
          {/* Welcome Message */}
          <motion.div
            className='text-center mb-12 mt-8 px-8'
            variants={staggerContainer}
            initial='initial'
            animate='animate'
          >
            <motion.h2
              className='text-xl lg:text-3xl font-semibold text-[var(--clinic-text)] mb-6'
              variants={fadeInUp}
              transition={{ delay: 0.3 }}
            >
              Welcome! My name is {clinic?.aiAgent?.name}, and I'm your personal
              booking assistant.
            </motion.h2>
            <motion.h3
              className='text-xl lg:text-2xl font-semibold text-[var(--clinic-text)]'
              variants={fadeInUp}
              transition={{ delay: 0.5 }}
            >
              Have you visited our practice before?
            </motion.h3>
          </motion.div>

          {/* Patient Type Selection */}
          <motion.div
            className='grid md:grid-cols-2 gap-6 mb-8 lg:px-20 px-8'
            variants={staggerContainer}
            initial='initial'
            animate='animate'
          >
            <motion.div
              variants={scaleIn}
              whileHover={buttonHover}
              whileTap={buttonTap}
              transition={{ delay: 0.7 }}
            >
              <SelectionButton
                isSelected={selectedPatientType === 'returning'}
                onClick={() => handleSelect('returning')}
                className='w-full aspect-square flex items-center justify-center text-center overflow-hidden'
              >
                <div className='flex flex-col items-center space-y-4'>
                  <div className='rounded-full flex items-center justify-center p-8 bg-white'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='72'
                      height='73'
                      viewBox='0 0 72 73'
                      fill='none'
                    >
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M23.6148 25.6629C27.0742 23.3566 31.7237 23.78 34.6803 26.7364C36.7948 28.8508 37.6827 31.8994 37.0339 34.8185L33.9337 48.7683C33.446 50.9635 31.499 52.5252 29.2503 52.5252C27.1853 52.5252 25.3519 51.2038 24.6988 49.2446L23.849 46.6951C23.6895 46.2164 23.2415 45.8936 22.7369 45.8936C22.2324 45.8936 21.7845 46.2164 21.6249 46.6951L20.7751 49.2446C20.122 51.2038 18.2886 52.5252 16.2236 52.5252C13.9748 52.5252 12.0279 50.9635 11.5401 48.7683L8.44013 34.8185C7.79141 31.8994 8.6791 28.8508 10.7937 26.7364C13.75 23.78 18.3997 23.3566 21.8591 25.6629C22.3835 26.0124 23.0904 26.0124 23.6148 25.6629ZM32.0007 29.4158C30.3379 27.7533 27.6926 27.4987 25.7168 28.8159C23.9196 30.0142 21.5544 30.0142 19.7571 28.8159C17.7813 27.4987 15.1359 27.7533 13.4732 29.4158C12.2748 30.6144 11.7717 32.342 12.1393 33.9965L15.2393 47.9463C15.3418 48.4075 15.751 48.7357 16.2236 48.7357C16.6575 48.7357 17.0428 48.4579 17.1801 48.0464L18.0299 45.4968C18.7052 43.471 20.6013 42.1041 22.7369 42.1041C24.8726 42.1041 26.7687 43.471 27.444 45.4968L28.2939 48.0464C28.431 48.4579 28.8164 48.7357 29.2503 48.7357C29.7229 48.7357 30.1321 48.4075 30.2345 47.9463L33.3346 33.9965C33.7022 32.342 33.199 30.6144 32.0007 29.4158Z'
                        fill='var(--clinic-icon-stroke)'
                      />
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M50.1408 10.5053C53.6002 8.199 58.2499 8.6224 61.2065 11.5788C63.321 13.6934 64.2089 16.7417 63.5601 19.6609L60.4599 33.6107C59.9722 35.8059 58.0252 37.3676 55.7765 37.3676C53.7116 37.3676 51.8779 36.0462 51.225 34.087L50.3754 31.5375C50.2155 31.0588 49.7676 30.736 49.2632 30.736C48.7584 30.736 48.3105 31.0588 48.151 31.5375L47.3014 34.087C46.6481 36.0462 44.8147 37.3676 42.7498 37.3676C40.5012 37.3676 38.5541 35.8059 38.0664 33.6107L34.9663 19.6609C34.3175 16.7417 35.2054 13.6934 37.3199 11.5788C40.2761 8.6224 44.9257 8.199 48.3852 10.5053C48.9096 10.8549 49.6167 10.8549 50.1408 10.5053ZM58.5269 14.2583C56.8641 12.5957 54.2187 12.3411 52.2428 13.6583C50.4459 14.8565 48.0805 14.8565 46.2831 13.6583C44.3073 12.3411 41.6619 12.5957 39.9994 14.2583C38.8008 15.4568 38.298 17.1844 38.6655 18.8389L41.7653 32.7887C41.868 33.2499 42.2773 33.5781 42.7498 33.5781C43.1837 33.5781 43.5691 33.3003 43.7063 32.8888L44.5563 30.3392C45.2316 28.3134 47.1274 26.9466 49.2632 26.9466C51.3989 26.9466 53.2948 28.3134 53.9701 30.3392L54.8201 32.8888C54.9572 33.3003 55.3426 33.5781 55.7765 33.5781C56.2491 33.5781 56.6583 33.2499 56.7607 32.7887L59.8608 18.8389C60.2284 17.1844 59.7252 15.4568 58.5269 14.2583Z'
                        fill='var(--clinic-icon-stroke)'
                      />
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M62.4813 39.6742L60.46 48.7693C59.9723 50.9646 58.0252 52.5266 55.7766 52.5266C53.7117 52.5266 51.878 51.2052 51.225 49.2461L50.3754 46.6965C50.2155 46.2179 49.7676 45.895 49.2632 45.895C48.7585 45.895 48.3106 46.2179 48.151 46.6965L47.3014 49.2461C46.6481 51.2052 44.8148 52.5266 42.7499 52.5266C40.5012 52.5266 38.5542 50.9646 38.0665 48.7693L36.0452 39.6742L39.7445 38.8523L41.7654 47.9474C41.8681 48.409 42.2773 48.7371 42.7499 48.7371C43.1838 48.7371 43.5692 48.4594 43.7063 48.0475L44.5563 45.4983C45.2316 43.472 47.1275 42.1056 49.2632 42.1056C51.399 42.1056 53.2948 43.472 53.9701 45.4983L54.8201 48.0475C54.9573 48.4594 55.3427 48.7371 55.7766 48.7371C56.2491 48.7371 56.6584 48.409 56.7607 47.9474L58.782 38.8523L62.4813 39.6742Z'
                        fill='var(--clinic-icon-stroke)'
                      />
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M58.7368 41.1575H34.1053V37.368H58.7368C66.0619 37.368 72 43.3061 72 50.6312V58.2101C72 61.3493 69.455 63.8944 66.3158 63.8944H5.68421C2.5449 63.8944 0 61.3493 0 58.2101V50.6312C0 44.0576 4.78065 38.6038 11.0549 37.5515L11.6819 41.2886C7.20277 42.0401 3.78947 45.9387 3.78947 50.6312V58.2101C3.78947 59.2564 4.63779 60.1049 5.68421 60.1049H66.3158C67.3621 60.1049 68.2105 59.2564 68.2105 58.2101V50.6312C68.2105 45.3991 63.969 41.1575 58.7368 41.1575Z'
                        fill='var(--clinic-icon-stroke)'
                      />
                    </svg>
                  </div>
                  <span
                    className={`text-xl font-medium text-center`}
                    style={{ color: 'var(--clinic-text-secondary)' }}
                  >
                    Yes, I'm a returning patient
                  </span>
                </div>
              </SelectionButton>
            </motion.div>

            <motion.div
              variants={scaleIn}
              whileHover={buttonHover}
              whileTap={buttonTap}
              transition={{ delay: 0.9 }}
            >
              <SelectionButton
                isSelected={selectedPatientType === 'new'}
                onClick={() => handleSelect('new')}
                className='w-full aspect-square flex items-center justify-center text-center overflow-hidden'
              >
                <div className='flex flex-col items-center space-y-4'>
                  <div className=' rounded-full flex items-center justify-center p-8 bg-white'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='72'
                      height='73'
                      viewBox='0 0 72 73'
                      fill='none'
                    >
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M23.615 22.2122C27.0744 19.8986 31.724 20.3234 34.6805 23.2891C36.795 25.4104 37.6829 28.4683 37.0342 31.3968L33.934 45.3906C33.4463 47.5928 31.4992 49.1594 29.2506 49.1594C27.1855 49.1594 25.3521 47.8338 24.6991 45.8685L23.8492 43.3109C23.6897 42.8307 23.2418 42.5068 22.7372 42.5068C22.2327 42.5068 21.7847 42.8307 21.6251 43.3109L20.7753 45.8685C20.1223 47.8338 18.2889 49.1594 16.2238 49.1594C13.9751 49.1594 12.0281 47.5928 11.5403 45.3906L8.44038 31.3968C7.79166 28.4683 8.67934 25.4104 10.7939 23.2891C13.7503 20.3234 18.4 19.8986 21.8594 22.2122C22.3837 22.5629 23.0907 22.5629 23.615 22.2122ZM32.001 25.9771C30.3382 24.3092 27.6929 24.0538 25.717 25.3752C23.9198 26.5771 21.5546 26.5771 19.7573 25.3752C17.7815 24.0538 15.1361 24.3092 13.4735 25.9771C12.275 27.1794 11.7719 28.9124 12.1396 30.5722L15.2396 44.5661C15.3421 45.0287 15.7512 45.3579 16.2238 45.3579C16.6578 45.3579 17.043 45.0793 17.1803 44.6664L18.0301 42.1088C18.7055 40.0766 20.6016 38.7054 22.7372 38.7054C24.8729 38.7054 26.7689 40.0766 27.4442 42.1088L28.2941 44.6664C28.4313 45.0793 28.8167 45.3579 29.2506 45.3579C29.7231 45.3579 30.1324 45.0287 30.2347 44.5661L33.3349 30.5722C33.7024 28.9124 33.1992 27.1794 32.001 25.9771Z'
                        fill='var(--clinic-icon-stroke)'
                      />
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M45.2366 28.2143C47.0791 24.4755 51.3176 22.5112 55.356 23.5967C58.2448 24.3732 60.5378 26.5762 61.4355 29.4377L65.7259 43.1116C66.4008 45.2632 65.4955 47.5969 63.5481 48.7248C61.7599 49.7603 59.5112 49.5319 57.9662 48.1576L55.9555 46.3691C55.5781 46.033 55.0286 45.9771 54.5917 46.2303C54.1548 46.4835 53.9282 46.9887 54.0293 47.4844L54.5682 50.1256C54.982 52.1552 54.0551 54.2229 52.2665 55.2587C50.3191 56.3866 47.8521 56.0061 46.3322 54.3437L36.6728 43.7799C34.6511 41.569 33.8959 38.4754 34.6697 35.5779C35.752 31.5265 39.5668 28.8265 43.7159 29.0949C44.345 29.1356 44.957 28.781 45.2366 28.2143ZM54.3753 27.2686C52.1043 26.6581 49.6859 27.7638 48.6335 29.8992C47.6759 31.8416 45.6277 33.0279 43.4723 32.8884C41.1023 32.7352 38.9389 34.2831 38.3303 36.5617C37.8915 38.2039 38.3197 39.9572 39.4653 41.2101L49.1246 51.7743C49.4441 52.1237 49.9629 52.2035 50.3717 51.9663C50.7477 51.7489 50.9424 51.3144 50.8557 50.8878L50.3168 48.2466C49.8886 46.1478 50.8473 44.0095 52.697 42.9383C54.5466 41.867 56.8718 42.1031 58.4699 43.5248L60.4805 45.3134C60.8053 45.6023 61.2775 45.6502 61.6534 45.4324C62.0627 45.1956 62.2529 44.7052 62.1112 44.2528L57.8207 30.579C57.3122 28.9573 56.0124 27.7087 54.3753 27.2686Z'
                        fill='var(--clinic-icon-stroke)'
                      />
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M0 47.2606C0 40.6659 4.78065 35.1952 11.0549 34.1392L11.6819 37.8881C7.20277 38.642 3.78947 42.5533 3.78947 47.2606V54.8635C3.78947 55.9131 4.63779 56.7642 5.68421 56.7642H66.3158C67.3621 56.7642 68.2105 55.9131 68.2105 54.8635V47.2606C68.2105 42.5533 64.7973 38.642 60.3182 37.8881L60.945 34.1392C67.2192 35.1952 72 40.6659 72 47.2606V54.8635C72 58.0126 69.455 60.5657 66.3158 60.5657H5.68421C2.5449 60.5657 0 58.0126 0 54.8635V47.2606Z'
                        fill='var(--clinic-icon-stroke)'
                      />
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M48.9448 19.5944C49.5254 20.4678 49.2901 21.6479 48.4192 22.2302L42.735 26.0317C41.8646 26.6139 40.688 26.378 40.1078 25.5045C39.5272 24.6311 39.7626 23.451 40.633 22.8687L46.3172 19.0672C47.188 18.4849 48.3643 18.721 48.9448 19.5944Z'
                        fill='var(--clinic-icon-stroke)'
                      />
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M40.3886 13.1446C41.3814 13.4765 41.918 14.553 41.5868 15.5488L39.6921 21.251C39.3613 22.2469 38.2885 22.7851 37.2956 22.4531C36.3028 22.1211 35.7662 21.0447 36.0974 20.0489L37.9921 14.3467C38.3229 13.3508 39.3957 12.8126 40.3886 13.1446Z'
                        fill='var(--clinic-icon-stroke)'
                      />
                    </svg>
                  </div>
                  <span
                    className={`text-xl font-medium text-center`}
                    style={{ color: 'var(--clinic-text-secondary)' }}
                  >
                    No, I'm a new patient
                  </span>
                </div>
              </SelectionButton>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Footer */}
      <Footer mobileColumnLayout={true} />
    </motion.div>
  );
}
